import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import { formatNumber } from '../../components/Common/Utilities';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import { ScanFailed } from '../../components/NonMappedInvoices/ScanFailed';
import Insights from './Insights';
import { PERIODS, DEFAULT_WEEKS, periodName } from './insightsModel';
import { MODULES } from '../shell/navModel';
import {
	QUEUES, STAGES, GUIDE, QUEUE_SOURCE, MINE_QUEUES, OLD_DAYS, bucket, isStuckScan, isRejected, queueChips, chipMatches,
	isMine, queueAge, formatAge, amountSummary, periodLabel, defaultQueue
} from './pipelineModel';

// Redesign (docs/ui_redesign/11-implementation-plan.md, "Workspace and shared grid"): the dashboard is a workspace, as in
// Dynamics 365 Finance (count tiles with one tabbed list) and SAP Fiori worklists (tabs with counts over one table).
// The queue cards are the tabs; below them is one classic gadget at a time, in Classic's full-screen view (all its
// columns and actions), showing only the chosen queue's rows. The gadget receives a view of the Dashboard whose `state`
// holds the filtered list; everything else (handlers, refs, setState) is the real component, so every action behaves
// as in Classic.

const HOT = { unmatched: 'warn', correct: 'warn', failed: 'bad' };
const ORDER = STAGES.flatMap(([, keys]) => keys);
const readUserId = () => { try { return (JSON.parse(localStorage.getItem('userInfo')) || {}).id || null; } catch (e) { return null; } };
const queueKey = modelDefId => `apworks.dashQueue.${modelDefId}`;
const readQueue = modelDefId => { try { const q = localStorage.getItem(queueKey(modelDefId)); return QUEUE_SOURCE[q] ? q : null; } catch (e) { return null; } };
const saveQueue = (modelDefId, q) => { try { localStorage.setItem(queueKey(modelDefId), q); } catch (e) { /* only a convenience */ } };
const readPref = (key, ok, fallback) => { try { const v = localStorage.getItem(key); return v != null && ok(v) ? v : fallback; } catch (e) { return fallback; } };
const savePref = (key, v) => { try { localStorage.setItem(key, String(v)); } catch (e) { /* only a convenience */ } };
const VIEW_KEY = m => `apworks.dashView.${m}`;
const WEEKS_KEY = m => `apworks.insightsWeeks.${m}`;
const COMPARE_KEY = m => `apworks.insightsCompare.${m}`;
const UPLOAD_TYPES = 'application/pdf,image/jpg,image/jpeg,image/png'; // the Invoice Queue gadget's own file input accepts these
const ANSWER_WAIT_MS = 12000; // a list that never loads (no rights) stops showing "…" after this

export default function DashboardPipeline({ thisRef, renderGadgets }) {
	const st = thisRef.state;
	const modelDefId = Number(thisRef.props.modelDefId);
	const moduleLabel = modelDefId === 1 ? 'Media' : modelDefId === 2 ? 'Production' : 'Broadcast';
	const [queue, setQueue] = useState(() => readQueue(modelDefId)); // null until the first queue with work is known
	const [chip, setChip] = useState(null);
	const [mode, setMode] = useState(() => readPref(VIEW_KEY(modelDefId), v => v === 'work' || v === 'insights', 'work'));
	const [insWeeks, setInsWeeks] = useState(() => Number(readPref(WEEKS_KEY(modelDefId), v => PERIODS.includes(Number(v)), DEFAULT_WEEKS)));
	const [insCompare, setInsCompare] = useState(() => readPref(COMPARE_KEY(modelDefId), v => v === 'on' || v === 'off', 'on') === 'on');
	const base = (MODULES.find(m => m.modelDefId === modelDefId) || MODULES[0]).base;
	const showMode = m => { setMode(m); savePref(VIEW_KEY(modelDefId), m); };
	const userId = useMemo(readUserId, []);
	const tabRefs = useRef({});
	const fileRef = useRef(null);
	// Scan failures, from the same API as the Non-Mapped Invoices "Scanner Failed" tab. Users without that screen's
	// rights get a 403, and the dashboard then shows nothing extra.
	const [scanFailed, setScanFailed] = useState(null);
	useEffect(() => {
		let live = true;
		httpGet(`/api/nonmappedinvoices/scannedinvoices/20/${modelDefId}`)
			.then(r => { if (live) setScanFailed(Array.isArray(r.data) ? r.data.length : null); }, () => { if (live) setScanFailed(null); });
		return () => { live = false; };
	}, [modelDefId]);

	const buckets = useMemo(() => bucket({
		queued: st.queuedInvoices || [],
		unmatched: st.filteredUnmatchedInvoices || [],
		pending: st.pendingApprovalInvoices || [],
		approved: st.approvedInvoices || []
	}), [st.queuedInvoices, st.filteredUnmatchedInvoices, st.pendingApprovalInvoices, st.approvedInvoices]);
	const rowsOf = k => (buckets[k] || []).map(e => e[0]);

	// Which gadgets' lists have answered since the dashboard opened (their loading flag went on and off). Until then a
	// card shows "…" rather than "0 · Nothing waiting". Dashboard.js sets gadgetFailed when a list couldn't load.
	// A list that is never fetched (no rights) counts as answered after a while.
	const [answered, setAnswered] = useState({});
	const started = useRef({});
	useEffect(() => {
		const gl = st.gadgetLoading || {};
		const next = {};
		[1, 2, 3, 4].forEach(g => { if (gl[g]) started.current[g] = true; else if (started.current[g]) next[g] = true; });
		if (Object.keys(next).some(g => !answered[g])) setAnswered(a => ({ ...a, ...next }));
	}, [st.gadgetLoading, answered]);
	useEffect(() => {
		const t = setTimeout(() => setAnswered(a => ({ 1: true, 2: true, 3: true, 4: true, ...a })), ANSWER_WAIT_MS);
		return () => clearTimeout(t);
	}, []);
	const failed = st.gadgetFailed || {};
	const allAnswered = [1, 2, 3, 4].every(g => answered[g] || failed[g]);

	// With no remembered tab, open the first queue that has work once every list has answered (Waiting to scan if none).
	useEffect(() => {
		if (queue || !allAnswered) return;
		setQueue(defaultQueue(buckets) || 'scan');
	}, [queue, buckets, allAnswered]);

	// The chosen queue's gadget is always in Classic's full-screen view. This also covers the gadget's own full-screen
	// toggle (hidden here) and anything else that returns Classic to its four-gadget view.
	const gadget = queue ? QUEUE_SOURCE[queue][0] : 0;
	useEffect(() => {
		if (gadget && Number(st.showGadget) !== gadget) thisRef.showFullScreen(gadget);
	}, [gadget, st.showGadget, thisRef]);
	const ready = gadget && Number(st.showGadget) === gadget;

	const ctx = useMemo(() => ({ isPartiallyPosted: row => thisRef.isPartiallyPostedRow(row), isMine: row => isMine(queue, row, userId) }), [thisRef, queue, userId]);
	const queueRows = useMemo(() => (queue ? buckets[queue].map(e => e[0]) : []), [queue, buckets]);
	const chips = useMemo(() => (queue ? queueChips(queue, queueRows, ctx) : []), [queue, queueRows, ctx]);
	const shownRows = useMemo(() => queueRows.filter(r => chipMatches(chip, r, ctx)), [queueRows, chip, ctx]);

	// The view the gadget renders from: the real component, with the chosen queue's list swapped into `state`.
	const viewState = useMemo(() => (queue ? { ...st, [QUEUE_SOURCE[queue][1]]: shownRows } : st), [st, queue, shownRows]);
	const viewStateRef = useRef(viewState);
	viewStateRef.current = viewState;
	const view = useMemo(() => new Proxy(thisRef, {
		get: (target, key) => (key === 'state' ? viewStateRef.current : Reflect.get(target, key, target)),
		set: (target, key, value) => Reflect.set(target, key, value, target)
	}), [thisRef]);

	const open = (q, withChip = null) => {
		setQueue(q);
		setChip(withChip);
		saveQueue(modelDefId, q);
	};
	const onTabKey = e => {
		const i = ORDER.indexOf(queue);
		const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: ORDER.length - 1 }[e.key];
		if (next == null) return;
		e.preventDefault();
		const k = ORDER[(next + ORDER.length) % ORDER.length];
		open(k);
		if (tabRefs.current[k]) tabRefs.current[k].focus();
	};

	// Upload works from every tab: this input hands its files to the Invoice Queue gadget's own upload handler.
	const upload = () => { if (fileRef.current) fileRef.current.click(); };
	const onFiles = e => { thisRef.fileUpload_changeHandler(e); if (queue !== 'scan') open('scan'); };

	const now = Date.now();
	const weeksOf = k => st['weeksToFilter' + QUEUE_SOURCE[k][0]];
	const periods = [1, 2, 3, 4].map(g => periodLabel(st['weeksToFilter' + g]));
	const samePeriod = periods.every(p => p === periods[0]);

	// Each card: the count, "for you", one line of age, and badges for what needs attention (what the Needs attention
	// strip listed before). Money moved to the queue's own header.
	const card = k => {
		const rows = rowsOf(k);
		const g = QUEUE_SOURCE[k][0];
		const out = { line: null, badges: [], mine: MINE_QUEUES.includes(k) ? rows.filter(r => isMine(k, r, userId)).length : 0 };
		if (failed[g]) { out.count = '—'; out.line = 'Couldn\u2019t load'; out.failed = true; return out; }
		if (!rows.length && !answered[g]) { out.count = '…'; out.line = 'Loading…'; return out; }
		if (k === 'scan') {
			const stuck = rows.filter(isStuckScan).length;
			if (stuck) out.badges.push({ text: `${stuck} stuck over 60 min`, tone: 'warn' });
			if (scanFailed) out.badges.push({ text: `${scanFailed} scan${scanFailed > 1 ? 's' : ''} failed`, tone: 'warn' });
		}
		if (!rows.length) { out.line = k === 'posted' ? 'None in this period' : 'Nothing waiting'; return out; }
		if (k === 'approve') {
			const rejected = rows.filter(r => isRejected(Number(r.invoiceDocumentStatusId))).length;
			if (rejected) out.badges.push({ text: `${rejected} rejected`, tone: 'bad' });
		}
		if (k === 'failed') out.badges.push({ text: `${rows.length} to repost`, tone: 'bad' });
		if (k === 'posted') { out.line = 'In the period'; return out; }
		const a = queueAge(k, rows, now);
		if (a) {
			out.line = `Oldest ${formatAge(a.oldest)}`;
			if (a.over && k !== 'ready' && k !== 'failed') out.badges.push({ text: `${a.over} over ${OLD_DAYS} days`, tone: 'warn' });
		}
		return out;
	};

	const q = queue ? QUEUES.find(x => x.key === queue) : null;
	// The chosen queue's summary, on the right of its header.
	const summary = (() => {
		if (!queue) return [];
		const out = [];
		const a = queueAge(queue, queueRows, now);
		if (a && queue !== 'posted') out.push({ key: 'old', text: <>Oldest <b>{formatAge(a.oldest)}</b></> });
		if (a && a.over && queue !== 'posted') out.push({ key: 'over', tone: 'warn', text: `${a.over} over ${OLD_DAYS} days` });
		const m = amountSummary(queueRows, formatNumber);
		if (m) out.push({ key: 'money', text: m.parts.map(([c, v], i) => <React.Fragment key={c}>{i ? ' · ' : ''}<b>{formatNumber(v)}</b> {c}</React.Fragment>) });
		if (MINE_QUEUES.includes(queue)) { const mine = queueRows.filter(r => isMine(queue, r, userId)).length; if (mine) out.push({ key: 'mine', text: <><b>{mine}</b> for you</> }); }
		return out;
	})();
	// The shown gadget's own period dropdown (Classic's control and handler), in the page header.
	const periodControl = mode === 'work' && gadget ? thisRef.getWeeksToFilterDropDown(gadget, gadget <= 2 ? ((st.filterText || {})[gadget] || '') : undefined) : null;
	// The chosen queue's gadget couldn't load: its own Refresh (Classic's handler) tries again.
	const retry = () => {
		const icon = document.querySelector('#apw2-gadgets svg[data-testid="RefreshIcon"]');
		const b = icon && icon.closest('button');
		if (b) b.click(); else if (gadget) thisRef.fetchDashboard(gadget, st['weeksToFilter' + gadget]);
	};
	// Scan failures come before the Waiting to scan list when there are any, so they aren't below a long list.
	const scanFirst = queue === 'scan' && scanFailed > 0;
	const scanSection = queue === 'scan' && scanFailed !== null ? (
		<section className="apw2-scanfailed" aria-label="Scan failed" id="apw2-scanfailed">
			<h2>Scan failed <span className="apw2-code">{scanFailed}</span></h2>
			<p>Files the scanner could not read. Tick Route for manual processing or Remove on a row, then Submit.</p>
			<ScanFailed info={{ inProgress: thisRef.props.inProgress, modelDefId }} />
		</section>
	) : null;
	const openFromInsights = (k, withChip) => { showMode('work'); if (k) open(k, withChip || null); };

	return (
		<div className={'apw2-dash in-queue apw2-dash-' + mode}>
			<div className="apw2-page-head apw2-dash-head">
				<div className="apw2-dash-title">
					<h1>{moduleLabel} dashboard</h1>
					<div className="apw2-viewsw" role="group" aria-label="Dashboard view">
						<button type="button" className={mode === 'work' ? 'on' : ''} aria-pressed={mode === 'work'} onClick={() => showMode('work')}>Work</button>
						<button type="button" className={mode === 'insights' ? 'on' : ''} aria-pressed={mode === 'insights'} onClick={() => showMode('insights')}>Insights</button>
					</div>
				</div>
				<div className="apw2-head-actions">
					{periodControl && <span className="apw2-head-period">{periodControl}</span>}
					{mode === 'insights' && (
						<>
							<label className="apw2-head-sel"><span>Period</span>
								<select value={insWeeks} onChange={e => { const w = Number(e.target.value); setInsWeeks(w); savePref(WEEKS_KEY(modelDefId), w); }}>
									{PERIODS.map(w => <option key={w} value={w}>{periodName(w)}</option>)}
								</select>
							</label>
							<label className="apw2-head-sel"><span>Compare</span>
								<select value={insCompare ? 'on' : 'off'} onChange={e => { setInsCompare(e.target.value === 'on'); savePref(COMPARE_KEY(modelDefId), e.target.value); }}>
									<option value="on">Previous period</option>
									<option value="off">No comparison</option>
								</select>
							</label>
						</>
					)}
					<input ref={fileRef} type="file" multiple accept={UPLOAD_TYPES} style={{ display: 'none' }} onChange={e => { showMode('work'); onFiles(e); }} data-testid="apw2-upload-input" />
					<button type="button" className="apw2-btn primary" onClick={upload}><CloudUploadOutlined fontSize="inherit" />Upload invoices</button>
				</div>
			</div>

			{mode === 'insights' && (
				<Insights modelDefId={modelDefId} base={base} weeks={insWeeks} compare={insCompare}
					queued={st.queuedInvoices || []} pending={st.pendingApprovalInvoices || []} onOpenQueue={openFromInsights} />
			)}

			<div className="apw2-work" hidden={mode !== 'work'}>
				<div className="apw2-pipe" role="tablist" aria-label="Invoice queues by stage" onKeyDown={onTabKey}>
					{STAGES.map(([stage, keys]) => (
						<div key={stage} className="apw2-stg" role="none" style={{ flexGrow: keys.length }}>
							<span className="apw2-stg-h" aria-hidden="true">{stage}</span>
							<div className="apw2-stg-row" role="none">
								{keys.map(k => {
									const x = QUEUES.find(y => y.key === k);
									const n = rowsOf(k).length;
									const on = queue === k;
									const c = card(k);
									return (
										<button type="button" role="tab" key={k} id={'apw2-tab-' + k} data-q={k} ref={el => { tabRefs.current[k] = el; }}
											className={'apw2-tile' + (on ? ' on' : '') + (n && HOT[k] ? ' ' + HOT[k] : '') + (c.failed ? ' is-failed' : '')}
											aria-selected={on} aria-controls="apw2-queue-panel" tabIndex={on || (!queue && k === ORDER[0]) ? 0 : -1} onClick={() => open(k)}>
											<span className="pl"><span className={'qd dot-' + x.tone} />{x.label}</span>
											<span className="pr"><span className="pn">{c.count != null ? c.count : n}</span>{c.mine > 0 && <span className="pm">{c.mine} for you</span>}</span>
											{c.line && <span className="ps">{c.line}</span>}
											{c.badges.length > 0 && <span className="pbs">{c.badges.map(b => <span key={b.text} className={'pb ' + b.tone}>{b.text}</span>)}</span>}
											{!samePeriod && <span className="pp">{periodLabel(weeksOf(k))}</span>}
										</button>
									);
								})}
							</div>
						</div>
					))}
				</div>

				{scanFirst && scanSection}
				<div id="apw2-queue-panel" className="apw2-queue-panel" role="tabpanel" aria-labelledby={queue ? 'apw2-tab-' + queue : undefined}>
					{q && (
						<div id="apw2-queue" className="apw2-queue-bar" role="region" aria-label={q.label}>
							<div className="apw2-queue-t">
								<h2 title={'Statuses: ' + q.code}>{q.label}</h2>
								<p>{GUIDE[queue]}</p>
							</div>
							{summary.length > 0 && <div className="apw2-queue-sum">{summary.map(x => <span key={x.key} className={x.tone || ''}>{x.text}</span>)}</div>}
							{chips.length > 0 && (
								<div className="apw2-chips apw2-seg" role="group" aria-label="Filter this queue">
									<button type="button" className={'apw2-fchip' + (!chip ? ' on' : '')} aria-pressed={!chip} onClick={() => setChip(null)}>All <span className="n">{queueRows.length}</span></button>
									{chips.map(c => <button type="button" key={c.key} className={'apw2-fchip' + (chip === c.key ? ' on' : '')} aria-pressed={chip === c.key} onClick={() => setChip(c.key)}>{c.label} <span className="n">{c.count}</span></button>)}
								</div>
							)}
						</div>
					)}
					{queue && failed[gadget] && (
						<div className="apw2-queue-error" role="alert">
							<b>Couldn’t load this list.</b> Check your connection, then try again.
							<button type="button" className="apw2-linkbtn" onClick={retry}>Try again</button>
						</div>
					)}
					{!ready && <div className="apw2-queue-loading" role="status">Loading…</div>}
					<div id="apw2-gadgets" className="apw2-gadgets" hidden={!ready}>{renderGadgets(queue ? view : thisRef)}</div>
				</div>

				{!scanFirst && scanSection}
			</div>
		</div>
	);
}
